// src/components/QueryHistory/QueryHistory.jsx
import React, { useState, useEffect } from 'react';
import { AlertCircle, Search } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { createApi } from '../../services/api';
import MainLayout from '../Layout/MainLayout';
import QuestionsList from '../QuestionsList/QuestionsList';

const QueryHistory = () => {
  const { token } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creditBalance, setCreditBalance] = useState(null);
  const [queries, setQueries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });
  const api = createApi(token);

  useEffect(() => {
    fetchBalance();
    fetchQueries(1);
  }, [token]);

  const fetchBalance = async () => {
    try {
      const response = await api.getBalance();
      setCreditBalance(response.balance);
    } catch (err) {
      console.error('Error fetching balance:', err);
    }
  };

  const fetchQueries = async (page = 1) => {
    try {
      setIsLoading(true);
      // Add pagination parameters to the API call
      const response = await api.get(`/user-questions/?page=${page}&page_size=${pagination.itemsPerPage}`);
      
      // Check if the response has pagination data
      if (response.results) {
        setQueries(response.results);
        setPagination({
          ...pagination,
          currentPage: page,
          totalPages: Math.ceil(response.count / pagination.itemsPerPage),
          totalItems: response.count
        });
      } else {
        // Fallback if the API doesn't support pagination yet
        setQueries(Array.isArray(response) ? response : []);
        setPagination({
          ...pagination,
          currentPage: 1,
          totalPages: 1,
          totalItems: Array.isArray(response) ? response.length : 0
        });
      }
      setError(null);
    } catch (err) {
      setError('Failed to load your research history');
      console.error('Error fetching queries:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchQueries(newPage);
    }
  };

  const filteredQueries = queries.filter(query => {
    // Search filter
    const matchesSearch = query.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (query.question_type && query.question_type.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // Status filter
    const matchesStatus = filterStatus === 'all' || query.status.toLowerCase() === filterStatus.toLowerCase();
    
    return matchesSearch && matchesStatus;
  });

  return (
    <MainLayout creditBalance={creditBalance}>
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-5">
            <h1 className="text-2xl font-bold text-white">Research History</h1>
            <p className="text-blue-100 mt-1">View and search your previous legal research queries</p>
          </div>

          {/* Search and Filter Bar */}
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search Input */}
              <div className="relative flex-grow">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search your questions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Status Filter */}
              <div className="flex-shrink-0">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="all">All Status</option>
                  <option value="completed">Completed</option>
                  <option value="processing">Processing</option>
                  <option value="error">Error</option>
                </select>
              </div>

              {/* Refresh Button */}
              <button
                onClick={() => fetchQueries(pagination.currentPage)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex-shrink-0"
              >
                Refresh
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {isLoading ? (
              <div className="animate-pulse space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-gray-100 rounded-lg p-6">
                    <div className="h-5 bg-gray-200 rounded w-1/3 mb-4"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded flex items-start">
                <AlertCircle className="h-5 w-5 text-red-400 mr-2 flex-shrink-0 mt-0.5" />
                <p className="text-red-700">{error}</p>
              </div>
            ) : (
              <QuestionsList 
                  questions={filteredQueries} 
                  pagination={pagination}
                  onPageChange={handlePageChange}
                />
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default QueryHistory;